import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import type Item from '../../../@types/item';
import useCalculatePayment from '../../../Data/Payments/useCalculatePayment';
import type { PaymentListItem } from '../../../Data/Payments/usePayments';
import { trackSoftConversionEvent as _trackSoftConversionEvent } from '../../../actions/tracking';
import { asHtml } from '../../../helpers/markdown';
import usePrevious from '../../../hooks/usePrevious';
import { softConversionEvents } from '../../../shared/tracking';
import Alert from '../../../styled-components/components/Alert';
import { Box } from '../../../styled-components/components/Box';
import { CreditorDisclaimer } from '../../../styled-components/components/CreditorDisclaimer';
import Modal from '../../../styled-components/components/Modal';
import { StackLarge, StackSmall } from '../../../styled-components/components/Stack';
import { Text } from '../../../styled-components/components/Text';
import CreditApplication from './credit-application';
import Details from './details';
import InputSlider from './input-slider';
import SelectSlider from './select-slider';

interface PaymentModalProps {
  id: string;
  branchId: string;
  item: Item;
  payment: PaymentListItem;
  showCreditorDisclaimer?: boolean;
  onClose: () => void;
}

const PaymentModal = ({
  id,
  branchId,
  item,
  payment,
  showCreditorDisclaimer,
  onClose,
}: PaymentModalProps) => {
  const { t } = useTranslation(['finance', 'validation', 'credit-application-vfs']);
  const [calculatePayment, request, calculatedPayment] = useCalculatePayment();
  const previousCalculatedPayment = usePrevious(calculatedPayment);
  const isPending = request.isLoading;
  const [creditApplicationView, setCreditApplicationView] = useState(false);

  const dispatch = useDispatch();
  const trackSoftConversionEvent = bindActionCreators(_trackSoftConversionEvent, dispatch);

  const activePayment = useMemo(
    () => calculatedPayment || previousCalculatedPayment || payment,
    [calculatedPayment, previousCalculatedPayment, payment],
  );

  const downPayment = activePayment.downPayment;
  const duration = activePayment.duration;
  const residual = activePayment.residual;

  const onOpenCreditApplicationView = () => setCreditApplicationView(true);
  const onCloseCreditApplicationView = () => setCreditApplicationView(false);

  const updateFinancialInformationIfNeeded = (
    durationValue: number,
    residualValue: number | undefined,
    downPaymentValue: number,
  ) => {
    const hasChanges =
      duration.current !== durationValue ||
      residual?.current !== residualValue ||
      downPayment.current !== downPaymentValue;

    if (!hasChanges) {
      return;
    }

    calculatePayment({
      id,
      data: {
        duration: durationValue,
        residual: residualValue,
        downPayment: downPaymentValue,
        branchId,
        financialOptionId: payment.financialOptionId,
      },
    });

    trackSoftConversionEvent(item, softConversionEvents.SOFT_CONVERSIONS_FINANCE, {
      financialInstitution: payment.financialInstitution,
    });
  };

  const onCashChange = (value: number) => {
    updateFinancialInformationIfNeeded(duration.current, residual?.current, value);
  };

  const onDurationChange = (value: number) => {
    updateFinancialInformationIfNeeded(value, residual?.current, downPayment.current);
  };

  const onResidualValueChange = (value: number) => {
    updateFinancialInformationIfNeeded(duration.current, value, downPayment.current);
  };

  return (
    <Modal
      heading={
        creditApplicationView
          ? t('heading', { ns: 'credit-application-vfs' })
          : t('loanOptions', { ns: 'finance' })
      }
      onClose={creditApplicationView ? onCloseCreditApplicationView : onClose}
      disableBackdropClose
    >
      {creditApplicationView ? (
        <CreditApplication
          id={id}
          downPayment={activePayment.downPayment.current}
          duration={activePayment.duration.current}
          residual={activePayment.residual?.current}
          financialInstitution={activePayment.financialInstitution}
        />
      ) : (
        <>
          {payment.description && (
            <StackLarge>
              <Text dangerouslySetInnerHTML={{ __html: asHtml(payment.description) }} />
            </StackLarge>
          )}
          <StackLarge>
            {!payment.downPayment.fixed && (
              <InputSlider
                title={t('downPayment', { ns: 'finance' })}
                min={downPayment.min}
                max={downPayment.max}
                step={downPayment.step}
                current={downPayment.current}
                defaultValue={downPayment.default}
                unit="kr"
                isPending={isPending}
                onChange={onCashChange}
                rangeLabel={{
                  min: ((downPayment.min / payment.vehiclePrice) * 100).toFixed(0),
                  max: ((downPayment.max / payment.vehiclePrice) * 100).toFixed(0),
                  unit: '%',
                }}
              />
            )}
            {!payment.duration.fixed && (
              <SelectSlider
                title={t('duration', { ns: 'finance' })}
                min={duration.min}
                max={duration.max}
                step={duration.step}
                defaultValue={duration.default}
                current={duration.current}
                isPending={isPending}
                onChange={onDurationChange}
                rangeLabel={{
                  min: duration.min,
                  max: duration.max,
                  unit: ` ${t('monthsUnit', { ns: 'finance' })}`,
                }}
              />
            )}
            {!payment.residual?.fixed && residual && (
              <InputSlider
                title={t('residual', { ns: 'finance' })}
                min={residual.min * 100}
                max={residual.max * 100}
                step={residual.step * 100}
                current={residual.current * 100}
                defaultValue={residual.default * 100}
                unit="%"
                isPending={isPending}
                onChange={(value: number) => onResidualValueChange(value / 100)}
                rangeLabel={{
                  min: (residual.min * 100).toFixed(0),
                  max: (residual.max * 100).toFixed(0),
                  unit: '%',
                }}
              />
            )}
          </StackLarge>
          <StackLarge>
            <StackSmall>
              <Details
                id={id}
                downPayment={activePayment.downPayment.current}
                duration={activePayment.duration.current}
                residual={activePayment.residual?.current}
                interest={activePayment.interest}
                effectiveInterest={activePayment.effectiveInterest}
                setupFee={activePayment.setupFee}
                registrationFee={activePayment.registrationFee}
                administrationFee={activePayment.administrationFee}
                totalCreditCost={activePayment.totalCreditCost}
                monthlyCost={activePayment.monthlyCost}
                financialInstitutionCtaUrl={activePayment.link}
                financialInstitutionCtaName={activePayment.linkText}
                financialInstitution={activePayment.financialInstitution}
                isPending={isPending}
                vehiclePrice={activePayment.vehiclePrice}
              />
            </StackSmall>
            {showCreditorDisclaimer && (
              <StackSmall>
                <Box>
                  <CreditorDisclaimer />
                </Box>
              </StackSmall>
            )}
          </StackLarge>

          {request.isError && (
            <StackLarge>
              <Alert tone="error">{t('unknownError', { ns: 'validation' })}</Alert>
            </StackLarge>
          )}
          {payment.imageUrl && (
            <StackLarge>
              <div className="m-t">
                <img
                  src={`${payment.imageUrl}?h=40`}
                  srcSet={`${payment.imageUrl}?h=80 2x`}
                  alt={payment.financialInstitution}
                  style={{ maxWidth: '80px' }}
                  className="img-center"
                />
              </div>
            </StackLarge>
          )}
          {payment.creditApplicationUrl && payment.monthlyCost > 0 && (
            <StackLarge>
              <div className="text-center">
                <button
                  type="button"
                  data-wx-button="primary"
                  onClick={onOpenCreditApplicationView}
                  title={t('applyForLoanPromise', { ns: 'finance' })}
                >
                  {t('applyForLoanPromise', { ns: 'finance' })}
                </button>
              </div>
            </StackLarge>
          )}
        </>
      )}
    </Modal>
  );
};

export default PaymentModal;
